import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { decimalToFixed2 } from '../../../../config/utils/calcUtils';
import { shouldShowTaxWithhold } from '../../../../config/utils/salesHelper';
import { CURRENCY } from '../../../../interfaces/enumerables';
import { InvoiceInterface } from '../../../../interfaces/invoice.interface';
import { PATHS } from '../../../../navigation/data';
import { useDocValues } from '../../../../services/hooks/useDocValues';
import { LinkTo } from '../../../atoms/LinkTo';

interface Props {
  doc: InvoiceInterface;
}

export const DocViewTotalCost = ({ doc }: Props) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isPublicDoc = useMemo(() => pathname.includes(PATHS.SHARED_DOC), [pathname]);
  const docValues = useDocValues(doc.products, doc.vatExceptionCategoryId, doc?.taxWithholdPercentage);

  const getCorrelatedMarkLink = useCallback(
    (mark: string) => (isPublicDoc ? '' : `${PATHS.SALES_VIEW_BY_MARK}/${mark}`),
    [isPublicDoc],
  );

  const getVatValuesView = useCallback(() => {
    return docValues?.valuesByVatCategory?.map?.((cat, index) => {
      return (
        <div className="flex-space-between-start-row-nowrap width-100 normalText1" key={`${cat.percentage}-${index}`}>
          <div className="width64">{cat.percentage}%</div>
          <div className="width180 textAlignEnd">
            {decimalToFixed2(cat.cleanCost)} {CURRENCY.EUR}
          </div>
          <div className="width120 textAlignEnd">
            {decimalToFixed2(cat.discount)} {CURRENCY.EUR}
          </div>
          <div className="width180 textAlignEnd">
            {decimalToFixed2(cat.finalCleanCost)} {CURRENCY.EUR}
          </div>
          <div className="width120 textAlignEnd">
            {decimalToFixed2(cat.total)} {CURRENCY.EUR}
          </div>
        </div>
      );
    });
  }, [docValues?.valuesByVatCategory]);

  /**
   * Customer logic to hide value of percentage for 14%
   * We are doing that for one Customer that want to add 2 tax withholds in one line (8% + 6%)
   */
  const getTaxWithholdPercentage = useCallback(() => {
    // Disabled for now since 14% is not available in prod
    // if (doc?.taxWithholdPercentage !== '14.00')
    return <div className="normalText0">({doc?.taxWithholdPercentage}%)</div>;
  }, [doc?.taxWithholdPercentage]);

  const getTaxWithholdSection = useCallback(() => {
    if (shouldShowTaxWithhold(doc)) {
      return (
        <div className="flex-space-between-start-row-nowrap width-100 padding-t-6">
          <div className="flex-center-start-column-nowrap">
            <div className="flex-start-center-row-nowrap">
              <div className="boldText1 padding-r-4">{t('SALE.TAX_WITHHOLD')}</div>
              {getTaxWithholdPercentage()}
            </div>
            <div className="normalText0">({t(`TAX_WITHHOLD.${doc?.taxWithhold?.name}_REASON`)})</div>
          </div>
          <div className="boldText1">
            -{docValues?.taxWithholdAmount} {CURRENCY.EUR}
          </div>
        </div>
      );
    }
  }, [doc, t, docValues?.taxWithholdAmount, getTaxWithholdPercentage]);

  return (
    <div className="totalCostContainer normalText1 simpleBorder">
      <div className="flex-space-between-start-row-nowrap width100 padding-b-6 boldText1">
        <div className="width64 boldText1">{t('SALE.VAT')} %</div>
        <div className="width180 textAlignEnd boldText1">{t('SALE.CLEAN_COST_BEFORE_DISCOUNT')}</div>
        <div className="width120 textAlignEnd boldText1">{t('SALE.DISCOUNT')}</div>
        <div className="width180 textAlignEnd boldText1">{t('SALE.CLEAN_COST')}</div>
        <div className="width120 textAlignEnd boldText1">
          {t('SALE.VAT')} {CURRENCY.EUR}
        </div>
      </div>
      {getVatValuesView()}
      <div className="separator" />
      <div className="flex-space-between-start-row-nowrap width-100 padding-b-6">
        <div className="boldText1">{t('SALE.TOTAL_CLEAN_VALUE')}</div>
        <div className="boldText1">
          {docValues?.cleanValueOfProducts} {CURRENCY.EUR}
        </div>
      </div>
      <div className="flex-space-between-start-row-nowrap width-100 padding-b-6 boldText1">
        <div className="boldText1">{t('SALE.TOTAL_VAT')}</div>
        <div className="boldText1">
          {docValues?.totalVat} {CURRENCY.EUR}
        </div>
      </div>
      <div className="flex-space-between-start-row-nowrap width-100 boldText1">
        <div className="boldText1">{t('SALE.TOTAL')}</div>
        <div className="boldText1">
          {docValues?.totalGrossAmount} {CURRENCY.EUR}
        </div>
      </div>
      {getTaxWithholdSection()}
      <div className="flex-space-between-start-row-nowrap width-100 boldText1 padding-t-20">
        <div className="boldText1">{t('SALE.PAYABLE_AMOUNT')}</div>
        <div className="boldText1">
          {docValues?.payableAmount} {CURRENCY.EUR}
        </div>
      </div>
      {doc?.correlatedInvoices?.length ? (
        <div className="flex-start-center-row-nowrap width-100 padding-t-2 normalText0" style={{ gap: 4 }}>
          <span className="boldText0">{t('SALE.CORRELATED_INVOICES_TITLE_VIEW')}:</span>
          <span>
            {doc?.correlatedInvoices?.map?.((c, index, marks) => (
              <span key={c.mark}>
                <LinkTo link={getCorrelatedMarkLink(c.mark)} data={{}} classes="link-reset">
                  <span className="text-blue">{c.mark}</span>
                </LinkTo>
                {index < marks.length - 1 ? ', ' : ''}
              </span>
            ))}
          </span>
        </div>
      ) : null}
    </div>
  );
};
